"use client";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useRef, useState } from "react";
import useResizeMeasure from "@/hooks/useResizeMeasure";

export default function Collapse({
	children,
	collapsedHeight = 120,
	defaultExpanded = false,
	expandText = "مشاهده بیشتر",
	collapseText = "بستن",
	expandButtonClassName = "text-blue-600",
	collapseButtonClassName = "text-blue-600",
	containerClassName = "",
	contentClassName = "",
	animationDuration = 300,
	buttonWrapperClassName = "",
	scrollBackOnCollapse = true,
	scrollBehavior = "smooth", // "smooth" | "auto"
	scrollBlock = "start", // "start" | "center" | "end" | "nearest"
	showFade = true,
	fadeHeight = 64,
}) {
	const contentRef = useRef(null);

	// anchor collapse
	const anchorRef = useRef(null);

	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const [shouldShowButton, setShouldShowButton] = useState(false);
	const [contentHeight, setContentHeight] = useState(0);

	const expandClickScrollYRef = useRef(null);

	const measure = () => {
		if (!contentRef.current) return;
		const fullHeight = contentRef.current.scrollHeight;
		setContentHeight(fullHeight);
		setShouldShowButton(fullHeight > collapsedHeight);
	};

	// scrollHeight فقط در ResizeObserver خوانده می‌شود (useResizeMeasure). ظرف در حالت بسته ارتفاع ثابت دارد، پس
	// فرزندان مستقیمش هم زیر نظرند تا تغییر محتوا یا عرض پنجره دیده شود؛ با تغییر deps دوباره observe و اندازه گرفته می‌شود.
	useResizeMeasure(
		() => (contentRef.current ? [contentRef.current, ...contentRef.current.children] : []),
		measure,
		[children, collapsedHeight, isExpanded]
	);

	const handleExpand = () => {
		expandClickScrollYRef.current = window.scrollY;
		setIsExpanded(true);
	};

	const handleCollapse = () => {
		if (scrollBackOnCollapse) {
			if (typeof expandClickScrollYRef.current === "number") {
				window.scrollTo({ top: expandClickScrollYRef.current, behavior: scrollBehavior });
			} else if (anchorRef.current) {
				anchorRef.current.scrollIntoView({ behavior: scrollBehavior, block: scrollBlock });
			}
		}
		setIsExpanded(false);
	};

	// در حالت بسته، انتهای محتوا با ماسک محو می‌شود تا کاربر متوجه شود
	// ادامه‌ای وجود دارد. چون ماسک است (نه گرادیانِ رنگی)، روی هر
	// پس‌زمینه‌ای - روشن، نوترال یا فوتر تیره - بدون تنظیم اضافه درست است.
	// ارتفاع فید از نصفِ ارتفاع بسته بیشتر نمی‌شود تا در کلپس‌های کوتاه
	// (مثل متن نظرات) محتوا بیش از حد محو نشود.
	const isFaded = showFade && shouldShowButton && !isExpanded;
	const fadeSize = isFaded
		? Math.max(0, Math.min(fadeHeight, Math.round(collapsedHeight / 2)))
		: 0;

	return (
		<div className={containerClassName}>
			<div ref={anchorRef} />

			<div
				ref={contentRef}
				className={`overflow-hidden ${fadeSize > 0 ? "collapse-fade" : ""} ${contentClassName}`}
				style={{
					maxHeight: isExpanded ? `${contentHeight}px` : `${collapsedHeight}px`,
					transition: `max-height ${animationDuration}ms ease`,
					...(fadeSize > 0 ? { "--collapse-fade-size": `${fadeSize}px` } : null),
				}}
			>
				{children}

				{shouldShowButton && isExpanded && (
					<div className={buttonWrapperClassName}>
						<button
							type="button"
							onClick={handleCollapse}
							className={`flex items-center gap-1 cursor-pointer ${collapseButtonClassName}`}
						>
							{collapseText}
							<ChevronUp size={16} />

						</button>
					</div>
				)}
			</div>

			{shouldShowButton && !isExpanded && (
				<div className={buttonWrapperClassName}>
					<button
						type="button"
						onClick={handleExpand}
						className={`flex items-center gap-1 cursor-pointer ${expandButtonClassName}`}
					>
						{expandText}
						<ChevronDown className="w-4 h-4 lg:w-5 lg:h-5" />

					</button>
				</div>
			)}
		</div>
	);
}
